import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { Link, useHistory, useParams } from "react-router-dom";

//UI
import { Form, Divider, Header, Loader, Button, Message } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import "react-phone-input-2/lib/style.css";
//Actions
import { createAgency } from "../../../redux/actions/catalogs/agency/createAgency";
import { initialStateAgency } from "../../../redux/actions/catalogs/agency/fetchAgency";
import { updateAgency } from "../../../redux/actions/catalogs/agency/updateAgency";
import { fetchAgencyFormCustom, initialStateAgencyFormCustom } from "../../../redux/slices/custom/catalogs/agency/customAgencyForm.slice";

//Resources
import { scrollToTop } from "../../resources/utils";
import PhoneInput from "react-phone-input-2";

export default function AgencyForm() {

  const commissionPercentRegex = new RegExp(/^(\d{1,2}(\.\d{1})?|100(\.0)?)$/);
  const { t } = useTranslation();
  let history = useHistory();
  let { agency_id } = useParams();
  const { setValue, register, handleSubmit, errors, control } = useForm({
    defaultValues: {
      phone: "",
      cellular_phone: "",
    },
  });

  const [countryCode, setCountryCode] = useState("");
  const agencyStateCustom = useSelector((state) => state.customAgencyForm);
  const agencyState = useSelector((state) => state.agencyReducer);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchAgencyFormCustom("", agency_id));
    scrollToTop();
    return () => {
      dispatch(initialStateAgency());
      dispatch(initialStateAgencyFormCustom());
    };
  }, []);

  useEffect(() => {
    setValue('name', agencyStateCustom.agency.name)
    setValue('identification_type_id', agencyStateCustom.agency?.identification_type_id)
    setValue('identification', agencyStateCustom.agency?.identification)
    setValue('address', agencyStateCustom.agency?.address)
    setValue('country_id', agencyStateCustom.agency?.country_id)
    setValue('city', agencyStateCustom.agency?.city)
    setValue('state', agencyStateCustom.agency?.state)
    setValue('postal_code', agencyStateCustom.agency?.postal_code)
    setValue('phone', agencyStateCustom.agency?.phone)
    setValue('cellular_phone', agencyStateCustom.agency?.cellular_phone)
    setValue('email', agencyStateCustom.agency?.email)
    setValue('commission', agencyStateCustom.agency?.commission)
    setValue('agency_type', agencyStateCustom.agency?.agency_type)
    setValue('active', agencyStateCustom.agency?.active)
    setValue('direct', agencyStateCustom.agency?.direct)
    setValue('without_rates', agencyStateCustom.agency?.without_rates)
    setValue('contact_person', agencyStateCustom.agency?.contact_person)
    setValue('contact_position', agencyStateCustom.agency?.contact_position)
    setValue('manager', agencyStateCustom.agency?.manager)
    setValue('manager_phone', agencyStateCustom.agency?.manager_phone)

  }, [agencyStateCustom.agency]);

  if (agencyStateCustom.fetching || agencyState.fetching) {
    return <Loader active inline />;
  }

  const onSubmit = (data) => {
    if (agency_id) {
      dispatch(updateAgency(agency_id, data));
    } else {
      dispatch(createAgency(data));
    }
  };

  const handleCountryChange = (e) => {
    const countryId = e.target.value;
    const code = agencyStateCustom.countries.find(country => country.id === countryId)?.code.toLowerCase()
    setCountryCode(code);
    setValue("phone", "");
    setValue("cellular_phone", "");
  };

  if (agencyState.message === "resource created successfully" || agencyState.message === "resource updated successfully") {
    history.push("/agencies");
  }

  let identificationTypesAgency = agencyStateCustom.identificationTypes.filter(
    (identificationType) => identificationType.type === "agency"
  );

  return (
    <>
      {agencyState.error ? (
        <Message negative content={t(agencyState.error)} />
      ) : (
        ""
      )}
      <Header as='h3' dividing>{t("agency_title")}</Header>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Header as='h4' dividing color='grey'>{t("general_information_tag")}</Header>
        <Form.Group widths="equal">
          <Form.Field required>
            <label>{t("agency_name")}</label>
            <input name="name" placeholder={t("agency_name")} maxLength={200} ref={register({ required: true })} autoFocus />
            {errors.name && (<span className="error_message">{t("required_information")}</span>)}
          </Form.Field>
          <Form.Field required>
            <label>{t("agency_identification_type")}</label>
            <select name="identification_type_id" aria-expanded="true" ref={register({ required: true })}>
              <option></option>
              {identificationTypesAgency.map((identificationType) => (
                <option key={identificationType.id} value={identificationType.id}>{identificationType.name}</option>
              ))}
            </select>
            {errors.identification_type_id && (<span className="error_message">{t("required_information")}</span>)}
          </Form.Field>
          <Form.Field>
            <label>{t("agency_identification")}</label>
            <input name="identification" placeholder={t("agency_identification")} maxLength={100} ref={register} />
          </Form.Field>
        </Form.Group>

        <Form.Group widths="equal">
          <Form.Field>
            <label>{t("agency_address")}</label>
            <input name="address" placeholder={t("agency_address")} maxLength={500} ref={register} />
          </Form.Field>
        </Form.Group>

        <Form.Group widths="equal">
          <Form.Field required>
            <label>{t("agency_country")}</label>
            <select onChange={handleCountryChange} name="country_id" aria-expanded="true" ref={register({ required: true })}>
              <option></option>
              {agencyStateCustom.countries.map((country) => (
                <option key={country.id} value={country.id}>{country.name}</option>
              ))}
            </select>
            {errors.country_id && (<span className="error_message">{t("required_information")}</span>)}
          </Form.Field>
          <Form.Field>
            <label>{t("agency_city")}</label>
            <input name="city" placeholder={t("agency_city")} maxLength={100} ref={register} />
          </Form.Field>
          <Form.Field>
            <label>{t("agency_state")}</label>
            <input name="state" placeholder={t("agency_state")} maxLength={100} ref={register} />
          </Form.Field>
          <Form.Field>
            <label>{t("agency_postal_code")}</label>
            <input name="postal_code" placeholder={t("agency_postal_code")} maxLength={100} ref={register} />
          </Form.Field>
        </Form.Group>
        <Form.Group widths="equal">
          <Form.Field>
            <label>{t("agency_phone")}</label>
            <Controller
              as={
                <PhoneInput
                  country={countryCode}
                  enableSearch={true}
                  inputClass="!w-full !border !rounded !pl-12 !py-2"
                  placeholder="Ingrese su número"
                />
              }
              name="phone"
              control={control}
              defaultValue=""
            />
          </Form.Field>
          <Form.Field>
            <label>{t("agency_cellphone")}</label>
            <Controller
              as={
                <PhoneInput
                  country={countryCode}
                  enableSearch={true}
                  inputClass="!w-full !border !rounded !pl-12 !py-2"
                  placeholder="Ingrese su número"
                />
              }
              name="cellular_phone"
              control={control}
              defaultValue=""
            />
          </Form.Field>
          <Form.Field>
            <label>{t("agency_email")}</label>
            <input name="email" placeholder={t("agency_email")} maxLength={200} ref={register} />
          </Form.Field>
        </Form.Group>

        <Form.Group widths="equal" >
          <Form.Field required>
            <label>{t("agency_sales_commission")}</label>
            <input name="commission" placeholder={t("agency_sales_commission")} maxLength={10} ref={register({ required: true, pattern: commissionPercentRegex })} />
            {errors.commission && errors.commission.type === "required" && (<span className="error_message">{t("required_information")}</span>)}
            {errors.commission && errors.commission.type === "pattern" && <span className="error_message">{t("valid_number")}</span>}
          </Form.Field>
          <Form.Field required>
            <label>{t("agency_type")}</label>
            <select name="agency_type" aria-expanded="true" ref={register({ required: true })} >
              <option></option>
              <option value="wholesaler">{t("wholesaler")}</option>
              <option value="retailer">{t("retailer")}</option>
            </select>
            {errors.agency_type && (<span className="error_message">{t("required_information")}</span>)}
          </Form.Field>
          <Form.Field>
            <label>{t("agency_direct")} <span className="text-[11px] text-gray-500">(comision 0%, en cualquier tipo de tarifa)</span></label>
            <div className="ui checkbox">
              <input type="checkbox" name="direct" ref={register} />
              <label></label>
            </div>
          </Form.Field>
          <Form.Field>
            <label>{t("agency_without_rates")} <span className="text-[11px] text-gray-500">(no aplica tarifas ni comision)</span></label>
            <div className="ui checkbox">
              <input type="checkbox" name="without_rates" ref={register} />
              <label></label>
            </div>
          </Form.Field>
          <Form.Field>
            <label>{t("active_tag")}</label>
            <div className="ui checkbox">
              <input type="checkbox" name="active" ref={register} />
              <label></label>
            </div>
          </Form.Field>
        </Form.Group>

        <Header as='h4' dividing color='grey'>{t("contact_information_tag")}</Header>
        <Form.Group widths="equal">
          <Form.Field>
            <label>{t("agency_contact_name")}</label>
            <input name="contact_person" placeholder={t("agency_contact_name")} maxLength={100} ref={register} />
          </Form.Field>
          <Form.Field>
            <label>{t("agency_position")}</label>
            <input name="contact_position" placeholder={t("agency_position")} maxLength={100} ref={register} />
          </Form.Field>
        </Form.Group>

        <Header as='h4' dividing color='grey'>{t("manager_information_tag")}</Header>
        <Form.Group widths="equal">
          <Form.Field>
            <label>{t("agency_name")}</label>
            <input name="manager" placeholder={t("agency_name")} maxLength={100} ref={register} />
          </Form.Field>
          <Form.Field>
            <label>{t("agency_phone")}</label>
            <Controller
              as={
                <PhoneInput
                  country={countryCode}
                  enableSearch={true}
                  inputClass="!w-full !border !rounded !pl-12 !py-2"
                  placeholder="Ingrese su número"
                />
              }
              name="manager_phone"
              control={control}
              defaultValue=""
            />
          </Form.Field>
        </Form.Group>

        <Divider variant="middle" />

        <Button primary type="submit" loading={agencyStateCustom.processing ? true : false}>{t("save_button")}</Button>
        <Link to={"/agencies"}><Button>{t("canceled_button")}</Button></Link>
      </Form>
    </>
  );
}
